/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import { getMe, hasGold } from 'app/gamma/src/selectors/members';
import { State } from 'app/gamma/src/modules/types';
import React, { useCallback, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import styles from './header.less';
import classNames from 'classnames';
import { usePopover, Popover, PopoverScreen } from '@trello/nachos';
import HeaderMemberMenu from 'app/gamma/src/components/popovers/header-member-menu';
import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';
import { defaultRouter } from 'app/common/router';
import ChangePassword from 'app/gamma/src/components/popovers/change-password';
import Help from 'app/gamma/src/components/popovers/help';
import LanguagePicker from 'app/src/components/LanguagePicker';
const format = forTemplate('header_member_menu');
const viewTitle = forNamespace('view title');

interface Props {
  hasMemberData: boolean;
  member: MemberModel | undefined;
}

enum Screen {
  MemberMenu,
  HelpMenu,
  LanguagePickerMenu,
  ChangePasswordMenu,
}

const HeaderMemberMenuButton: FunctionComponent<Props> = ({
  hasMemberData,
  member,
}) => {
  const { name = '', username = '', id = '', avatarSource = undefined } =
    member || {};
  const userTitle = `${name}${username && ` (${username})`}`;

  const { toggle, push, hide, triggerRef, popoverProps } = usePopover<
    HTMLButtonElement
  >({
    initialScreen: Screen.MemberMenu,
  });
  const onClick = useCallback(() => {
    if (!hasMemberData) {
      return;
    }

    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.MEMBER_MENU,
      method: 'by using member menu button in header',
    });
    toggle();
  }, [hasMemberData, toggle]);

  return (
    <>
      <HeaderButton
        // this className is needed by Desktop for the My Cards MenuItem
        className={classNames(
          'js-open-header-member-menu',
          styles.memberButton,
        )}
        onClick={onClick}
        ref={triggerRef}
        testId={HeaderTestIds.MemberMenuButton}
        title={userTitle}
      >
        <MemberAvatar avatarSource={avatarSource} idMember={id} size={32} />
      </HeaderButton>
      <Popover
        {...popoverProps}
        title={`${member?.name} (${member?.username})`}
      >
        <PopoverScreen
          id={Screen.MemberMenu}
          testId={HeaderTestIds.MemberMenuPopover}
          noHorizontalPadding
        >
          <HeaderMemberMenu
            onClickHelp={() => push(Screen.HelpMenu)}
            onClickLanguagePicker={() => push(Screen.LanguagePickerMenu)}
            onClickChangePassword={() => push(Screen.ChangePasswordMenu)}
            onClickShortcuts={() => {
              defaultRouter.setRoute('/shortcuts');
              hide();
            }}
            onClickMenuLink={hide}
          />
        </PopoverScreen>
        <PopoverScreen
          id={Screen.HelpMenu}
          title={format('help')}
          size="xlarge"
          noHorizontalPadding
        >
          <Help />
        </PopoverScreen>
        <PopoverScreen
          id={Screen.LanguagePickerMenu}
          title={viewTitle('change locale')}
          noHorizontalPadding
        >
          <LanguagePicker memberId={member?.id ?? ''} />
        </PopoverScreen>
        <PopoverScreen id={Screen.ChangePasswordMenu} noHorizontalPadding>
          <ChangePassword />
        </PopoverScreen>
      </Popover>
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    hasMemberData: !!getMe(state),
    member: getMe(state),
    hasCrown: hasGold(state),
  };
};

export default connect(mapStateToProps)(HeaderMemberMenuButton);
